import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { ComponentRef, Injectable, InjectionToken, Injector } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { ConfirmationModalOverlayRef } from '../classes/confirmation-modal.ref';
import { ConfirmationModalData } from '../interfaces/confirmation-modal-data';
import { CONFIRMATION_MODAL_DATA } from '../tokens/confirmation-data.token';
import { customOverlayPosition } from '../interfaces/customPosition-modal-data';
import { CustomOverlayAnimationConfig } from '../interfaces/animation-modal-data';
import { CommonFunctionsService } from './common-functions.service';
import { Observable, filter, of, switchMap, Subscription } from 'rxjs';
import { ConfirmationComponent } from '../components/confirmation/confirmation.component';
import { FocusHelperService } from './FocusHelper.service';

export const FILE_PREVIEW_DIALOG_DATA = new InjectionToken<ConfirmationModalConfig>('FILE_PREVIEW_DIALOG_DATA');

export interface ConfirmationModalConfig {
  hasBackdropClick?: boolean;
  hasEscapeKeyClose?: boolean;
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  data?: ConfirmationModalData | any;
  isCentered?: boolean;
  customOverlayPosition?: customOverlayPosition | null;
  isAnimation?: CustomOverlayAnimationConfig | null;
  closeOnBackButton?: boolean; // New config option
  autoClose?: boolean;
  autoCloseAfterSec?: number;
}

const DEFAULT_CONFIG: ConfirmationModalConfig = {
  hasBackdropClick: false,
  hasEscapeKeyClose: false,
  hasBackdrop: true,
  backdropClass: 'my-overlay-modal-template-backdrop',
  isCentered: true,
  data: {},
  closeOnBackButton: true // Default to true
};


@Injectable()
export class ConfirmationModalService {
  public dialogRef!: ConfirmationModalOverlayRef;
  private backButtonSubscriptions = new Map<ConfirmationModalOverlayRef, Subscription>();
  // private localDEFAULT_CONFIG!: ConfirmationModalConfig;

  constructor(
    private overlay: Overlay,
    private _CommonFunctionsService: CommonFunctionsService,
    private _FocusHelperService: FocusHelperService,
    private location: Location,
    private router: Router
  ) { }

  open<T>(component: ComponentType<T>, config: ConfirmationModalConfig = {}) {
    const localDEFAULT_CONFIG = { ...DEFAULT_CONFIG, ...config };
    const overlayRef = this.createOverlay(localDEFAULT_CONFIG);
    const dialogRef = new ConfirmationModalOverlayRef(overlayRef);

    // Set up back button handling if enabled
    if (localDEFAULT_CONFIG.closeOnBackButton) {
      let routerSubscription = this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart && this.dialogRef) {
          this.dialogRef.close(); // Close the modal on navigation
          routerSubscription.unsubscribe();
        }
      });
    }

    const overlayComponent = this.attachModalContainer(overlayRef, localDEFAULT_CONFIG, dialogRef, component);

    overlayRef.backdropClick().subscribe((_) => {
      if (localDEFAULT_CONFIG.hasBackdropClick) {
        dialogRef.close(config);
      }
    });

    overlayRef.keydownEvents().subscribe((_: KeyboardEvent) => {
      if (_.key == 'Escape') {
        if (localDEFAULT_CONFIG.hasEscapeKeyClose) {
          dialogRef.close(config);
        }
      }
    });

    this.dialogRef = dialogRef;
    return dialogRef;
  }

  private attachModalContainer<T>(overlayRef: OverlayRef, config: ConfirmationModalConfig,
    dialogRef: ConfirmationModalOverlayRef, component: ComponentType<T>) {
    const injector = this.createInjector(config, dialogRef);

    const containerPortal = new ComponentPortal(component, null, injector);
    const containerRef: ComponentRef<T> = overlayRef.attach(containerPortal);

    // Handle post-attach focus logic
    this.focusElementInComponent(containerRef, config);

    return containerRef.instance;
  }

  private focusElementInComponent<T>(
    containerRef: ComponentRef<T>,
    config: ConfirmationModalConfig
  ) {
    const rootElement = containerRef.location.nativeElement as HTMLElement;
    const selector = config.data?.autoFocusSelector;
    this._FocusHelperService.autoFocus(rootElement, selector);
  }

  private createInjector(config: ConfirmationModalConfig, dialogRef: ConfirmationModalOverlayRef): Injector {
    return Injector.create({
      providers: [{
        provide: ConfirmationModalOverlayRef, useValue: dialogRef
      },
      {
        provide: CONFIRMATION_MODAL_DATA, useValue: config.data
      }]
    });
  }

  private getOverlayConfig(config: ConfirmationModalConfig): OverlayConfig {
    let positionStrategy = this.overlay.position()
      .global();

    positionStrategy = this._CommonFunctionsService.setPositionStrategy(config, positionStrategy);
    let panelCls: any = this._CommonFunctionsService.setMdlAnimation(config, config.panelClass);

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: panelCls,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy
    });

    return overlayConfig;
  }

  private createOverlay(config: ConfirmationModalConfig) {
    // Returns an OverlayConfig
    const overlayConfig = this.getOverlayConfig(config);

    // Returns an OverlayRef
    return this.overlay.create(overlayConfig);
  }

  init<T>(Component: ComponentType<T>, _ConfirmationModalConfig: ConfirmationModalConfig = {}): Observable<any> {
    let confirmationRef!: ConfirmationModalOverlayRef;
    confirmationRef =
      this.open<T>(
        Component,
        _ConfirmationModalConfig
      );
    return confirmationRef.events.pipe(
      filter((event) => {
        return !!event
      }),
      switchMap((v) => {
        v['initRef'] = confirmationRef;
        return of(v)
        // return new Promise<void>((resolve, reject) => {
        //   resolve(v)
        // })
      })
    )

  }

  init2<T>(Component: ComponentType<T>, _ConfirmationModalConfig: ConfirmationModalConfig = {}): { ref: ConfirmationModalOverlayRef, events$: Observable<any> } {
    const confirmationRef = this.open<T>(Component, _ConfirmationModalConfig);
    const events$ = confirmationRef.events.pipe(
      filter((event) => !!event),
      switchMap((v) => {
        v['initRef'] = confirmationRef;
        return of(v);
      })
    );
    return { ref: confirmationRef, events$ };
  }


  openSubmitConfirmationModal(title: string, icon: 'error' | 'success' | 'info' | 'warning' | 'custom', msg?: string) {
    let customConfig: ConfirmationModalConfig = {
      data: {
        icon,
        title,
        msg,
        btnConfirm: '',
        btnClose: 'Okay',
        btnAction: '',
      },
    };

    let confirmationRef =
      this.open<ConfirmationComponent>(
        ConfirmationComponent,
        customConfig
      );
    confirmationRef.events.pipe(
      filter((event) => {
        return !!event
      }),
    ).subscribe((events) => {
      confirmationRef?.close(customConfig);
    });

  };

}
